import { logEvent } from 'firebase/analytics';
import * as firebaseConfig from '../firebase/config';
import BANK from '../data/question-bank.json';

/**
 * Arrivals: what the app does with a link (BRAND_SPEC §15.1).
 *
 *   /?q=<bank id>        an ad or post. The question is typed into the
 *                        composer, parked as the heading and asked once:
 *                        Rob's approved first answer when there is one (no
 *                        model call), otherwise AI Rob, live.
 *   /q/<bank id>         the share/print alias, when the app itself is served
 *                        there (the static /q/ pages forward to /?q=<id>).
 *   ?q=<anything else>   only fills the composer. Nothing is sent: a link's
 *                        words are never asked for someone (§15.1; the link
 *                        should carry an ID, never the words).
 *   #ask=<text>          free text typed on thenet.org: fills the composer too.
 *   ?next=meet|talk|seer the handoff panel (TEAM_APP_SPEC §8.1, Lane B).
 *   utm_*, campaign      kept for this tab only, for content-free counts; and
 *                        whether they say a paid ad sent the visitor (`ad`,
 *                        isAdArrival): the front page then holds nothing back
 *                        (ChatView's first view plays for everyone else).
 *
 * Everything is read once, at load, into sessionStorage, and the address bar
 * is cleaned with history.replaceState so a reload never asks twice and the
 * words never travel on in a Referer.
 */

const STORE_KEY = 'apr-arrival-v1';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
const BANK_ID = /^q\d{2}m?$/;
const MAX_PREFILL = 500;

/**
 * "Came from an ad": the one rule, functions/adArrival.js, said again here
 * word for word so the browser can act on it before anything is sent (the
 * server keeps its own copy for the counts and the conversations the team
 * keeps). A utm_medium decides when there is one: paid placements are ads;
 * organic_social, print, referral, email and anything else are not. Without a
 * medium, only Meta's own automatic source codes (utm_source=fb|ig|msg|an)
 * with a campaign count. scripts/check-analytics.js asks both copies the same
 * questions and fails when they differ: change the lists there and here.
 */
const PAID_MEDIUMS = new Set(['paid', 'paid_social', 'paidsocial', 'paid-social', 'paid_search', 'paidsearch',
  'paid_video', 'cpc', 'ppc', 'cpm', 'cpv', 'display', 'ctv', 'ooh', 'audio']);
const META_AUTO_SOURCES = new Set(['fb', 'ig', 'msg', 'an']);

/** True for a link's { src, med, camp } (utm_source, utm_medium, utm_campaign) that a paid ad sent. */
export function isAdArrival(a) {
  const med = String((a && a.med) || '').toLowerCase();
  if (med) return PAID_MEDIUMS.has(med);
  return META_AUTO_SOURCES.has(String((a && a.src) || '').toLowerCase()) && Boolean(a && a.camp);
}

/** The same, for an arrival's utm fields as readArrival keeps them. */
function adOf(utm) {
  const u = utm || {};
  return isAdArrival({ src: u.utm_source, med: u.utm_medium, camp: u.utm_campaign });
}

const byId = new Map(BANK.questions.map((q) => [q.id, q]));

/** A bank question that can land a visitor, or null (unknown or retired). */
export function bankQuestion(id) {
  const q = byId.get(String(id || '').trim());
  return q && !q.retired ? q : null;
}

/** Order of the human steps for a question's category (§11.1 ladder). */
export function nextStepOrder(question) {
  if (question?.nextStepOrder?.length) return question.nextStepOrder;
  return BANK.categories[question?.category] || ['meet', 'talk', 'seer'];
}

/**
 * The ladder's words: TEAM_APP_SPEC's names, BRAND_SPEC §11.1's descriptions (§13.5 clocks).
 * PENDING Rob's decision (BRAND_SPEC §20.2 decision 2): whether "Seer" keeps its name in
 * public. This is the spec's default; change the label here if the test says otherwise.
 */
export const NEXT_STEPS = {
  talk: { label: 'Talk first', description: 'A real person from The Net texts or calls you within two days.' },
  seer: { label: 'Save me a seat at Seer', description: 'A small table for honest questions about God, over dinner.' },
  meet: { label: 'Meet me Sunday', description: 'Someone meets you at the door. Coffee, a seat, no spotlight.' },
};

/** The welcome screen's starters: the bank's seeker set, four at a time (§11.1). */
const SEEKER_STARTERS = ['q02', 'q12', 'q14', 'q16', 'q17', 'q19', 'q24'];
// The question the ads run on ("What if God is real?", Oct 2026): always the
// first starter, so a visitor from the ad sees it waiting. The other three are
// drawn at random from the rest of the seeker set.
const PINNED_STARTER = 'q02';

// "More questions" draws from a deeper pool: the seeker set, then more of the
// bank's questions that read as a visitor's own (one variant each; never the
// ad-copy hooks like "Are you looking for God?").
const MORE_STARTERS = [...SEEKER_STARTERS, 'q15', 'q21', 'q25', 'q20', 'q09m', 'q13m', 'q22m', 'q06m', 'q03m', 'q12m', 'q07m', 'q23'];
let moreDeck = [];

function shuffled(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The next four for "More questions": none of the ones on screen, and the
 * whole pool before any repeats (a shuffled deck, dealt in turn).
 */
export function nextStarters(current = [], n = 4) {
  const onScreen = new Set(current);
  const out = [];
  for (let guard = 0; out.length < n && guard < 200; guard++) {
    if (!moreDeck.length) moreDeck = shuffled(MORE_STARTERS.map(bankQuestion).filter(Boolean));
    const q = moreDeck.shift();
    if (!onScreen.has(q.text) && !out.some((x) => x.id === q.id)) out.push(q);
  }
  return out;
}

export function sampleStarters(n = 4) {
  const pinned = bankQuestion(PINNED_STARTER);
  const pool = shuffled(SEEKER_STARTERS.filter((id) => id !== PINNED_STARTER).map(bankQuestion).filter(Boolean));
  return (pinned ? [pinned, ...pool] : pool).slice(0, n);
}

function plainText(value) {
  return String(value || '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_PREFILL);
}

function safeDecode(value) {
  try {
    return decodeURIComponent(String(value).replace(/\+/g, ' '));
  } catch (e) {
    return '';
  }
}

/**
 * Reads the arrival from the current URL once, stores it for this tab and
 * cleans the address bar. Returns { landing, prefill, next, utm, campaign }.
 * `landing` is a bank question to ask, or null. Safe to call more than once:
 * after the first call the URL is clean and the stored arrival is returned.
 */
export function readArrival() {
  if (typeof window === 'undefined') return emptyArrival();
  let stored = null;
  try { stored = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { stored = null; }

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const pathMatch = url.pathname.match(/^\/q\/(q\d{2}m?)\/?$/);
  const rawQ = params.get('q') || (pathMatch ? pathMatch[1] : null);
  const hash = url.hash.startsWith('#ask=') ? safeDecode(url.hash.slice(5)) : null;
  const touched = rawQ != null || hash != null || params.has('next') || params.has('campaign')
    || UTM_KEYS.some((k) => params.has(k)) || Boolean(pathMatch);

  if (!touched) return stored ? { ...emptyArrival(), ...withAd(stored), fresh: false } : emptyArrival();

  const question = rawQ ? bankQuestion(rawQ) : null;
  // An unknown or retired ID opens the normal welcome screen: never an error,
  // and never "q99" typed into the box.
  const looksLikeId = Boolean(rawQ && BANK_ID.test(rawQ.trim()));
  let prefill = null;
  if (!question) {
    if (hash != null) prefill = plainText(hash) || null;
    else if (rawQ && !looksLikeId) prefill = plainText(rawQ) || null;
  }
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 80);
  });
  const next = ['meet', 'talk', 'seer'].includes(params.get('next')) ? params.get('next') : null;
  const campaign = (params.get('campaign') || '').replace(/[^\w-]/g, '').slice(0, 64) || null;
  const arrival = {
    landing: question ? question.id : null,
    // A non-bank ?q= or #ask= only ever fills the composer.
    prefill,
    next,
    utm,
    campaign,
    // A paid ad sent them (isAdArrival): kept with the arrival, so the tab
    // reloaded still knows.
    ad: adOf(utm),
    at: Date.now(),
  };

  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(arrival)); } catch (e) { /* storage blocked */ }

  // Clean the address bar: these words and IDs never ride along on a reload
  // or a Referer. Anything else in the URL is left alone.
  ['q', 'next', 'campaign', ...UTM_KEYS].forEach((k) => params.delete(k));
  if (pathMatch) url.pathname = '/';
  if (hash != null) url.hash = '';
  try {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (e) { /* sandboxed: harmless */ }

  return { ...arrival, fresh: true };
}

function emptyArrival() {
  return { landing: null, prefill: null, next: null, utm: {}, campaign: null, ad: false, fresh: false };
}

/** A stored arrival with its `ad` answer, worked out again for one kept before the field existed. */
function withAd(stored) {
  return typeof stored.ad === 'boolean' ? stored : { ...stored, ad: adOf(stored.utm) };
}

/** The arrival this tab opened with, read back without touching the address bar (visitor analytics). */
export function storedArrival() {
  try {
    return { ...emptyArrival(), ...withAd(JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null') || {}) };
  } catch (e) {
    return emptyArrival();
  }
}

/**
 * What the team's copy of a conversation records about where it came from
 * (functions/conversations.js): the device class by viewport width, and the
 * campaign and bank-question id of the link this tab opened with, if any.
 * utm_source and utm_medium go too, so the server can tell a paid ad from an
 * organic post by the same rule analytics uses (functions/adArrival.js); it
 * keeps only the yes or no. IDs and campaign names only; never words.
 */
export function conversationContext() {
  const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const device = width < 700 ? 'phone' : width < 1100 ? 'tablet' : 'desktop';
  const a = storedArrival();
  const utm = a.utm || {};
  const campaign = utm.utm_campaign || a.campaign || null;
  const question = a.landing || null;
  const src = utm.utm_source || null;
  const med = utm.utm_medium || null;
  return { device, ...(campaign || question || src || med ? { campaign: { campaign, question, src, med } } : {}) };
}

/** True for the rest of a tab that arrived from a campaign link (no giving ask, §11.1). */
export function isCampaignSession() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    return Boolean(stored && (stored.landing || Object.keys(stored.utm || {}).length || stored.campaign));
  } catch (e) {
    return false;
  }
}

/**
 * First-party, content-free events (BRAND_SPEC §15.3): IDs and campaign
 * fields only, never chat text, names or contact details. Firebase Analytics
 * starts asynchronously and only on real hosts; until it is ready the event
 * waits a few seconds, then is dropped.
 */
export function logArrivalEvent(name, params = {}) {
  let tries = 0;
  const attempt = () => {
    try {
      // A live binding: config.js fills it in once Analytics is supported.
      if (firebaseConfig.analytics) logEvent(firebaseConfig.analytics, name, params);
      else if (tries++ < 10) setTimeout(attempt, 1000);
    } catch (e) { /* analytics unavailable: fine */ }
  };
  attempt();
}
